import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ArrowLeft, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

interface AnalysisErrorProps {
  error: string;
  videoId?: string;
  assetId?: string;
}

export function AnalysisError({ error, videoId, assetId }: AnalysisErrorProps) {
  const isInvalidId = error.includes('invalid input syntax') || error.includes('Bad Request');
  
  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/swing-lab/upload">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Upload
          </Link>
        </Button>
      </div>

      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Analysis Failed to Load</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>{error}</p>
          
          {isInvalidId && (
            <div className="mt-4 space-y-2">
              <p><strong>Possible causes:</strong></p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Using Mux playback ID instead of database video ID</li>
                <li>Using Mux asset ID instead of database video ID</li>
                <li>Video not found in your account</li>
                <li>Video still processing</li>
              </ul>
            </div>
          )}
          
          <div className="mt-4 space-y-2">
            <p><strong>Debug Information:</strong></p>
            <div className="text-xs font-mono bg-muted p-2 rounded">
              {videoId && <div>Video ID: {videoId}</div>}
              {assetId && <div>Asset ID: {assetId}</div>}
              <div>URL: {window.location.href}</div>
            </div>
          </div>
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Button asChild>
          <Link to="/admin/video-debug">
            <Search className="h-4 w-4 mr-2" />
            Find Correct Video ID
          </Link>
        </Button>
        
        <Button variant="outline" asChild>
          <Link to="/library">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Browse Video Library
          </Link>
        </Button>
      </div>

      <Alert>
        <AlertDescription>
          <strong>How to fix:</strong>
          <ol className="list-decimal list-inside text-sm mt-2 space-y-1">
            <li>Go to the <Link to="/admin/video-debug" className="underline">Video Debug Tool</Link></li>
            <li>Search using your playback ID or asset ID</li>
            <li>Copy the correct <strong>Video ID</strong> (UUID format)</li>
            <li>Use format: <code>/analysis/ANALYSIS_ID</code> (where ANALYSIS_ID is the runId from swing_analysis_runs table)</li>
            <li>Legacy format (redirects): <code>/swing-lab/analysis?runId=RUN_ID</code></li>
          </ol>
        </AlertDescription>
      </Alert>
    </div>
  );
}
